<template>
  <div class="completion-record-list">
    <div class="page-header">
      <h2>生产入库记录</h2>
      <a-space>
        <a-button @click="handleExport" :loading="exportLoading">
          <template #icon><DownloadOutlined /></template>
          导出Excel
        </a-button>
      </a-space>
    </div>

    <a-card class="summary-card" :loading="summaryLoading">
      <a-row :gutter="24">
        <a-col :span="6">
          <a-statistic
            title="入库总数量"
            :value="summary.totalQuantity || 0"
            :value-style="{ color: '#1890ff', fontSize: '28px', fontWeight: 'bold' }"
            suffix="件"
          />
          <div class="stat-sub">共 {{ summary.totalRecords || 0 }} 条记录</div>
        </a-col>
        <a-col :span="6">
          <a-statistic
            title="入库总工价"
            :value="summary.totalAmount || 0"
            :precision="2"
            :value-style="{ color: '#52c41a', fontSize: '28px', fontWeight: 'bold' }"
            prefix="¥"
          />
          <div class="stat-sub">数量 × 工价之和</div>
        </a-col>
        <a-col :span="6">
          <a-statistic
            title="涉及商品"
            :value="summary.productCount || 0"
            :value-style="{ fontSize: '28px', fontWeight: 'bold' }"
            suffix="款"
          />
        </a-col>
        <a-col :span="6">
          <a-statistic
            title="涉及工厂"
            :value="summary.factoryCount || 0"
            :value-style="{ fontSize: '28px', fontWeight: 'bold' }"
            suffix="家"
          />
        </a-col>
      </a-row>
    </a-card>

    <a-card class="filter-card">
      <a-form layout="inline">
        <a-form-item label="入库日期">
          <a-range-picker
            v-model:value="searchForm.dateRange"
            format="YYYY-MM-DD"
            style="width: 250px"
          />
        </a-form-item>
        <a-form-item label="工厂">
          <a-select
            v-model:value="searchForm.factoryId"
            placeholder="请选择工厂"
            allow-clear
            show-search
            :filter-option="filterOption"
            style="width: 180px"
          >
            <a-select-option
              v-for="factory in factories"
              :key="factory.id"
              :value="factory.id"
              :label="factory.name"
            >
              {{ factory.name }}
            </a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="仓库">
          <a-select
            v-model:value="searchForm.warehouseId"
            placeholder="请选择仓库"
            allow-clear
            style="width: 160px"
          >
            <a-select-option
              v-for="warehouse in warehouses"
              :key="warehouse.id"
              :value="warehouse.id"
              :label="warehouse.name"
            >
              {{ warehouse.name }}
            </a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="商品名称">
          <a-input
            v-model:value="searchForm.productName"
            placeholder="请输入商品名称"
            allow-clear
            style="width: 180px"
          />
        </a-form-item>
        <a-form-item label="订单编号">
          <a-input
            v-model:value="searchForm.preProductionNo"
            placeholder="请输入订单编号"
            allow-clear
            style="width: 180px"
          />
        </a-form-item>
        <a-form-item label="颜色">
          <a-input
            v-model:value="searchForm.color"
            placeholder="颜色"
            allow-clear
            style="width: 100px"
          />
        </a-form-item>
        <a-form-item label="尺码">
          <a-input
            v-model:value="searchForm.size"
            placeholder="尺码"
            allow-clear
            style="width: 100px"
          />
        </a-form-item>
        <a-form-item>
          <a-space>
            <a-button type="primary" @click="handleSearch">
              <template #icon><SearchOutlined /></template>
              查询
            </a-button>
            <a-button @click="handleReset">重置</a-button>
          </a-space>
        </a-form-item>
      </a-form>
    </a-card>

    <a-card>
      <a-table
        :columns="columns"
        :data-source="records"
        :loading="loading"
        :pagination="pagination"
        @change="handleTableChange"
        row-key="id"
        :scroll="{ x: 1400 }"
        :sticky="{ offsetScroll: 0, getContainer: getScrollContainer }"
        size="middle"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'completionDate'">
            {{ formatDate(record.completionDate) }}
          </template>
          <template v-if="column.key === 'sku'">
            {{ record.color || '默认' }} / {{ record.size || '默认' }}
          </template>
          <template v-if="column.key === 'preProductionNo'">
            <a @click="$router.push(`/production/preproduction/detail/${record.preProductionId}`)">
              {{ record.preProductionNo }}
            </a>
          </template>
          <template v-if="column.key === 'unitPrice'">
            {{ Number(record.unitPrice || 0).toFixed(2) }}
          </template>
          <template v-if="column.key === 'totalAmount'">
            {{ Number(record.totalAmount || 0).toFixed(2) }}
          </template>
          <template v-if="column.key === 'action'">
            <a-button type="link" size="small" @click="openEdit(record)">编辑</a-button>
            <a-button type="link" size="small" danger @click="handleDelete(record)">删除</a-button>
          </template>
        </template>
      </a-table>
    </a-card>

    <a-modal
      v-model:open="editVisible"
      title="编辑入库记录"
      @ok="handleEditSubmit"
      :confirm-loading="editSubmitting"
      width="520px"
    >
      <a-form layout="vertical" v-if="editForm.id">
        <a-descriptions :column="2" size="small" bordered style="margin-bottom: 16px">
          <a-descriptions-item label="入库日期">{{ formatDate(editForm.completionDate) }}</a-descriptions-item>
          <a-descriptions-item label="工厂">{{ editForm.factoryName }}</a-descriptions-item>
          <a-descriptions-item label="商品">{{ editForm.productName }}</a-descriptions-item>
          <a-descriptions-item label="SKU">{{ editForm.color || '默认' }} / {{ editForm.size || '默认' }}</a-descriptions-item>
          <a-descriptions-item label="仓库">{{ editForm.warehouseName }}</a-descriptions-item>
          <a-descriptions-item label="数量">{{ editForm.completionQuantity }}</a-descriptions-item>
        </a-descriptions>
        <a-form-item label="工价">
          <a-input-number
            v-model:value="editForm.unitPrice"
            :min="0"
            :precision="2"
            style="width: 200px"
            placeholder="请输入工价"
          />
          <span style="margin-left: 12px; color: #999;">
            工价金额：¥ {{ ((editForm.unitPrice || 0) * (editForm.completionQuantity || 0)).toFixed(2) }}
          </span>
        </a-form-item>
        <a-form-item label="备注">
          <a-textarea
            v-model:value="editForm.remark"
            :rows="3"
            :maxlength="500"
            show-count
            placeholder="请输入备注"
          />
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import { ref, shallowRef, reactive, onMounted, createVNode } from 'vue'
import { message, Modal } from 'ant-design-vue'
import { ExclamationCircleOutlined } from '@ant-design/icons-vue'
import {
  SearchOutlined,
  DownloadOutlined
} from '@ant-design/icons-vue'
import { preProductionApi, factoryApi, warehouseApi } from '@/api'

export default {
  name: 'CompletionRecordList',
  components: {
    SearchOutlined,
    DownloadOutlined
  },
  setup() {
    const loading = ref(false)
    const exportLoading = ref(false)
    const summaryLoading = ref(false)
    const editVisible = ref(false)
    const editSubmitting = ref(false)
    const editForm = reactive({
      id: null,
      remark: '',
      unitPrice: 0,
      completionQuantity: 0,
      completionDate: '',
      factoryName: '',
      productName: '',
      color: '',
      size: '',
      warehouseName: ''
    })
    const records = shallowRef([])  // 纯展示大列表：shallowRef 免深度响应式
    const factories = ref([])
    const warehouses = ref([])
    const summary = ref({
      totalQuantity: 0,
      totalAmount: 0,
      totalRecords: 0,
      productCount: 0,
      factoryCount: 0
    })

    const searchForm = reactive({
      dateRange: [],
      factoryId: undefined,
      warehouseId: undefined,
      productName: '',
      preProductionNo: '',
      color: '',
      size: ''
    })

    const pagination = reactive({
      current: 1,
      pageSize: 20,
      total: 0,
      showSizeChanger: true,
      showTotal: (total) => `共 ${total} 条`
    })

    const columns = [
      { title: '入库日期', dataIndex: 'completionDate', key: 'completionDate', width: 110, fixed: 'left' },
      { title: '工厂', dataIndex: 'factoryName', key: 'factoryName', width: 120 },
      { title: '订单编号', dataIndex: 'preProductionNo', key: 'preProductionNo', width: 160 },
      { title: '商品', dataIndex: 'productName', key: 'productName', width: 180, ellipsis: true },
      { title: 'SKU', key: 'sku', width: 120 },
      { title: '仓库', dataIndex: 'warehouseName', key: 'warehouseName', width: 110 },
      { title: '数量', dataIndex: 'completionQuantity', key: 'completionQuantity', width: 80, align: 'right' },
      { title: '工价', key: 'unitPrice', width: 90, align: 'right' },
      { title: '工价金额', key: 'totalAmount', width: 110, align: 'right' },
      { title: '备注', dataIndex: 'remark', key: 'remark', ellipsis: true },
      { title: '创建时间', dataIndex: 'createTime', key: 'createTime', width: 160 },
      { title: '操作', key: 'action', width: 130, fixed: 'right' }
    ]

    const filterOption = (input, option) => {
      return (option.label || '').toLowerCase().includes(input.toLowerCase())
    }

    const getScrollContainer = () => document.querySelector('.content') || window

    const formatDate = (yyyymmdd) => {
      if (!yyyymmdd || yyyymmdd.length !== 8) return yyyymmdd || ''
      return `${yyyymmdd.substring(0, 4)}-${yyyymmdd.substring(4, 6)}-${yyyymmdd.substring(6, 8)}`
    }

    const buildParams = () => {
      const params = {
        factoryId: searchForm.factoryId || undefined,
        warehouseId: searchForm.warehouseId || undefined,
        productName: searchForm.productName || undefined,
        preProductionNo: searchForm.preProductionNo || undefined,
        color: searchForm.color || undefined,
        size: searchForm.size || undefined,
        pageNo: pagination.current,
        pageSize: pagination.pageSize
      }
      if (searchForm.dateRange && searchForm.dateRange.length === 2) {
        params.startDate = searchForm.dateRange[0].format('YYYYMMDD')
        params.endDate = searchForm.dateRange[1].format('YYYYMMDD')
      }
      return params
    }

    const loadRecords = async () => {
      loading.value = true
      try {
        const result = await preProductionApi.completionList(buildParams())
        if (result.code === '0') {
          records.value = result.data.records || []
          pagination.total = result.data.total || 0
        } else {
          message.error(result.msg || '加载入库记录失败')
        }
      } catch (error) {
        console.error('加载入库记录失败:', error)
        message.error('加载入库记录失败')
      } finally {
        loading.value = false
      }
    }

    const loadSummary = async () => {
      summaryLoading.value = true
      try {
        const params = buildParams()
        delete params.pageNo
        delete params.pageSize
        const result = await preProductionApi.completionSummary(params)
        if (result.code === '0') {
          summary.value = result.data || {}
        }
      } catch (error) {
        console.error('加载汇总失败:', error)
      } finally {
        summaryLoading.value = false
      }
    }

    const loadFactories = async () => {
      try {
        const result = await factoryApi.all()
        if (result.code === '0') {
          factories.value = result.data || []
        }
      } catch (error) {
        console.error('加载工厂失败:', error)
      }
    }

    const loadWarehouses = async () => {
      try {
        const result = await warehouseApi.list()
        if (result.code === '0') {
          warehouses.value = result.data || []
        }
      } catch (error) {
        console.error('加载仓库失败:', error)
      }
    }

    const handleTableChange = (pag) => {
      pagination.current = pag.current
      pagination.pageSize = pag.pageSize
      loadRecords()
    }

    const handleSearch = () => {
      pagination.current = 1
      loadRecords()
      loadSummary()
    }

    const handleReset = () => {
      searchForm.dateRange = []
      searchForm.factoryId = undefined
      searchForm.warehouseId = undefined
      searchForm.productName = ''
      searchForm.preProductionNo = ''
      searchForm.color = ''
      searchForm.size = ''
      pagination.current = 1
      loadRecords()
      loadSummary()
    }

    const openEdit = (record) => {
      editForm.id = record.id
      editForm.remark = record.remark || ''
      editForm.unitPrice = Number(record.unitPrice || 0)
      editForm.completionQuantity = record.completionQuantity || 0
      editForm.completionDate = record.completionDate || ''
      editForm.factoryName = record.factoryName || ''
      editForm.productName = record.productName || ''
      editForm.color = record.color || ''
      editForm.size = record.size || ''
      editForm.warehouseName = record.warehouseName || ''
      editVisible.value = true
    }

    const handleEditSubmit = async () => {
      if (!editForm.id) return
      editSubmitting.value = true
      try {
        const result = await preProductionApi.completionUpdate({
          id: editForm.id,
          remark: editForm.remark,
          unitPrice: editForm.unitPrice
        })
        if (result.code === '0') {
          message.success('保存成功')
          editVisible.value = false
          loadRecords()
          loadSummary()
        } else {
          message.error(result.msg || '保存失败')
        }
      } catch (error) {
        console.error('保存失败:', error)
        message.error('保存失败')
      } finally {
        editSubmitting.value = false
      }
    }

    const handleDelete = (record) => {
      Modal.confirm({
        title: '确认删除',
        icon: createVNode(ExclamationCircleOutlined),
        content: createVNode('div', null, [
          createVNode('p', { style: 'margin: 0 0 6px;' }, `删除入库记录：${record.productName || ''} ${record.color || ''}/${record.size || ''} × ${record.completionQuantity || 0}`),
          createVNode('p', { style: 'margin: 0; color: #ff7875;' }, '将同步回滚库存数量、扣减预生产订单已完成进度。')
        ]),
        okText: '删除',
        okType: 'danger',
        cancelText: '取消',
        async onOk() {
          try {
            const result = await preProductionApi.completionDelete({ id: record.id })
            if (result.code === '0') {
              message.success('删除成功')
              loadRecords()
              loadSummary()
            } else {
              message.error(result.msg || '删除失败')
            }
          } catch (error) {
            console.error('删除失败:', error)
            message.error(error.message || '删除失败')
          }
        }
      })
    }

    const handleExport = async () => {
      exportLoading.value = true
      try {
        const params = buildParams()
        delete params.pageNo
        delete params.pageSize
        const response = await preProductionApi.completionExport(params)
        const blob = new Blob([response.data], { type: 'application/vnd.ms-excel' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        const now = new Date()
        const timestamp = now.getFullYear().toString() +
          String(now.getMonth() + 1).padStart(2, '0') +
          String(now.getDate()).padStart(2, '0') + '_' +
          String(now.getHours()).padStart(2, '0') +
          String(now.getMinutes()).padStart(2, '0') +
          String(now.getSeconds()).padStart(2, '0')
        link.download = `生产入库记录_${timestamp}.xlsx`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        message.success('导出成功')
      } catch (error) {
        console.error('导出失败:', error)
        message.error('导出失败')
      } finally {
        exportLoading.value = false
      }
    }

    onMounted(() => {
      loadFactories()
      loadWarehouses()
      loadRecords()
      loadSummary()
    })

    return {
      loading,
      exportLoading,
      summaryLoading,
      records,
      factories,
      warehouses,
      summary,
      searchForm,
      pagination,
      columns,
      filterOption,
      getScrollContainer,
      formatDate,
      handleSearch,
      handleReset,
      handleExport,
      handleTableChange,
      editVisible,
      editSubmitting,
      editForm,
      openEdit,
      handleEditSubmit,
      handleDelete
    }
  }
}
</script>

<style scoped>
.completion-record-list {
  padding: 0;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.page-header h2 {
  margin: 0;
}

.summary-card {
  margin-bottom: 16px;
}

.stat-sub {
  font-size: 13px;
  color: #999;
  margin-top: 4px;
}

.filter-card {
  margin-bottom: 16px;
}
</style>
